<template>
    <div>
        <!-- Teleport the modal to the document body -->
        <Teleport to="body">
            <transition name="modal" @after-leave="afterLeave">
                <div
                    v-if="isOpen"
                    class="fixed inset-0 z-[99999999]"
                    role="dialog"
                    aria-modal="true"
                    tabindex="-1"
                    ref="modal"
                    @keydown.escape="closeModal"
                >
                    <!-- Background overlay -->
                    <div
                        class="absolute inset-0 bg-primary/75"
                        @click="handleCloseOnBgClick"
                        aria-hidden="true"
                    ></div>
                    <!-- Centering wrapper -->
                    <div
                        class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 max-h-screen py-24 px-4 md:p-0 mx-auto"
                        :style="modalStyle"
                    >
                        <!-- Modal content with dynamic width -->
                        <div class="rounded-lg shadow-lg z-10 relative mx-auto">
                            <!-- Close Button -->
                            <button
                                @click="closeModal"
                                class="absolute -top-10 right-0 text-white hover:text-gray-700 text-2xl h-8 w-8 cursor-pointer select-none z-[9999]"
                                aria-label="Close modal"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    fill="currentColor"
                                    class="h-full w-full object-contain"
                                >
                                    <path
                                        d="M41 39C31.6 29.7 16.4 29.7 7 39S-2.3 63.6 7 73l183 183L7 439c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l183-183L407 473c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-183-183L441 73c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-183 183L41 39z"
                                    />
                                </svg>
                            </button>
                            <!-- Slot for injected content -->
                            <div>
                                <slot></slot>
                            </div>
                        </div>
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import usePublicStore from "@stores/publicStore";
const publicStore = usePublicStore();

const props = defineProps({
    popupSettings: {
        type: Object,
        default: null,
    },
    name: {
        type: String,
        required: true,
    },
    closeOnBgClick: {
        type: Boolean,
        default: true,
    },
    isOpenExternal: {
        type: Boolean,
        default: false,
    },
    buttonClasses: {
        type: String,
    },
    buttonText: {
        type: String,
        default: "Open Modal",
    },
    disableScroll: {
        type: Boolean,
        default: false,
    },
    // Width prop that controls the modal’s size
    width: {
        type: [String, Number],
        default: "800px",
    },
});

const isOpen = computed(() => publicStore.modalOpen === props.name);
const modal = ref(null);

// Computed style that ensures a proper CSS width string
const modalStyle = computed(() => ({
    width: "96%",
    maxWidth:
        typeof props.width === "number" ? `${props.width}px` : props.width,
}));

function handleCloseOnBgClick() {
    if (!props.closeOnBgClick) return;
    closeModal();
}

function closeModal() {
    publicStore.closeModal();
    document.removeEventListener("keydown", handleTab);
}

function afterLeave() {
    if (props.disableScroll) {
        document.body.style.overflow = "";
    }
}

function handleTab(e) {
    if (e.key !== "Tab") return;
    const focusableElements = modal.value?.querySelectorAll(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
    );
    if (!focusableElements || focusableElements.length === 0) return;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
        if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
        }
    } else {
        if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
        }
    }
}

onMounted(() => {
    if (props.popupSettings) {
        const now = new Date();
        const start = props.popupSettings.schedule_start
            ? new Date(props.popupSettings.schedule_start)
            : null;
        const end = props.popupSettings.schedule_end
            ? new Date(props.popupSettings.schedule_end)
            : null;
        if ((!start || now >= start) && (!end || now <= end)) {
            setTimeout(() => {
                openModal();
            }, props.popupSettings.delay || 0);
        }
    }
});

const handleSubmitted = () => {
    closeModal();
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}
.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
</style>
